import React from 'react';
import AddIcon from '@material-ui/icons/AddCircle';
import { Button, Typography } from '@material-ui/core';

function steps() {
    return [
        {
            content: ({ goTo }) => (
                <div>
                    <br />
                    Welcome to Plan.
                    <br /><br />
                    Here you can create and share your plans and reports.
                    <br /><br />
                    <div className='tour-to-step'>
                        <button onClick={() => {
                            window.openColumnTypeAccordion?.(true);
                            setTimeout(() => {goTo(5)}, 300);
                        }}>Column Types</button>

                        <button onClick={() => {
                            window.openColumnsAccordion?.(true);
                            setTimeout(() => {goTo(8)}, 300);
                        }}>Columns</button>

                        <button onClick={() => {
                            window.openGridsAccordion?.(true);
                            setTimeout(() => {goTo(10)}, 300);
                        }}>Grids</button>

                        <button onClick={() => {
                            window.openMasksAccordion?.(true);
                            setTimeout(() => {goTo(13)}, 300);
                        }}>Masks</button>

                        <button onClick={() => {
                            window.openSaveConfigAccordion?.(true);
                            setTimeout(() => {goTo(19)}, 300);
                        }}>Saving Configs</button>

                        <button onClick={() => {
                            window.openReportAccordion?.(true);
                            setTimeout(() => {goTo(23)}, 300);
                        }}>Reports</button>
                    </div>
                </div>
            ),
        },
        {
            selector: '.planStep-drawing-import',
            content: () => (
                <div>
                    <br />
                    Lets begin by importing a Drawing file.
                    <br /><br />
                    The button to do this is located in the left toolbar.
                </div>
            ),
        },
        {
            selector: '.planStep-drawing-file-select',
            content: () => (
                <div>
                    <br />
                    Click this button to select a drawing file from your computer.
                    <br /><br />
                    Afterwards you can adjust the resolution and scale settings.
                </div>
            ),
        },
        {
            selector: '.planStep-drawing-file-select2',
            content: () => (
                <div>
                    <br />
                    Adjusting the resolution and scale settings here will help ensure that area calculations are accurate.
                    <br /><br />
                    Once you are happy with the settings, click Close to finish importing the drawing. -
                    <strong>Please note that changing these settings after importing may affect area calculations and/or design setups.</strong>
                    <br /><br />
                    Close the dialog to continue.
                </div>
            ),
        },
        {
            content: () => (
                <div>
                    <br />
                    After importing your drawing, you can start designing your plan.
                    <br /><br />
                    Everyone does this differently but lets take a look at the right tool bar which we try to keep organised by workflow
                </div>
            ),
            action: () => {
                window.openColumnTypeAccordion?.(true);
            }
        },
        {
            selector: '.planStep-column-type',
            content: () => (
                <div>
                    <br />
                    This column type section allows you to set pre-defined column settings for easier usage.
                    <br /><br />
                    This means you can set this up once and then quickly create the same column multiple times in your plan.
                    <br /><br />
                    Click the <span><AddIcon className="tour-icon" /></span> icon to get started
                </div>
            ),
        },
        {
            selector: '.planStep-column-type2',
            content: () => (
                <div style={{ display: "inline", lineHeight: "1.6" }}>
                    <br />
                    Use this menu to select your luminaire for the new column type.
                    <br /><br />
                    You can also set a custom name for your luminaire here or later in the settings.
                    <br /><br />
                    Finish your selection by pressing the{" "}
                    <Button
                        style={{
                            margin: "0 6px",
                            padding: "2px 10px",
                            verticalAlign: "middle",
                            display: "inline-flex"
                        }}
                        color="primary"
                        variant="contained"
                        size="small"
                    >
                        <Typography variant="button">Add</Typography>
                    </Button>
                    button after clicking on a file.
                </div>
            ),
        },
        {
            selector: '.planStep-column-type',
            content: () => (
                <div>
                    <br />
                    After importing your column type it will appear in this list.
                    <br /><br />
                    You can edit, view and delete your column types at any time from here. You also come to the section to add columns to your plan.
                </div>
            ),
            action: () => {
                window.openColumnsAccordion?.(true);
            }
        },
        {
            selector: '.planStep-column',
            content: () => (
                <div>
                    <br />
                    Once you have created your column type and add columns to your plan they will appear in this list in the default group.
                    <br /><br />
                    In these settings you can edit, change the specific column to a mast, focus on the column and delete it.
                </div>
            ),
        },
        {
            selector: '.planStep-column',
            content: () => (
                <div>
                    <br />
                    When handling groups use our tooltips for each button to understand their function.
                    <br /><br />
                    As a basic breakdown:
                    <br />
                    <ol>
                        <li>Select all - Select every column in the group</li>
                        <li>Sync - Synchronises any movement or rotation changes to every column (even not selected)</li>
                        <li>Flip Horizontal - Flips the position of the columns in the group horizontally about the center point</li>
                        <li>Flip Vertical - Flips the position of the columns in the group vertically about the center point</li>
                    </ol>
                    {/* For more in depth support use our help function located next to the tour button. */}
                </div>
            ),
            action: () => {
                window.openGridsAccordion?.(true);
            }
        },
        {
            selector: '.planStep-grids',
            content: () => (
                <div>
                    <br />
                    This section allows you to create and manage grids.
                    <br /><br />
                    You can select the standard you want to work with as well as create grids of various widths and heights. - Press the <span><AddIcon className="tour-icon" /></span> icon to create your first grid.
                    <br /><br />
                    Please note that the entire drawing is your working area, so you can create grids, masks and columns anywhere on the drawing.
                </div>
            ),
        },
        {
            selector: '.planStep-grid-list',
            content: () => (
                <div>
                    <br />
                    Here you can edit, view the results of, lock and delete each individual grid.
                    <br /><br />
                    I will let you explore the settings later on your own as there are many options to choose from depending on your needs.
                </div>
            ),
        },
        {
            content: () => (
                <div>
                    <br />
                    Let continue to the mask section which is where most of your time will likely be spent.
                    <br /><br />
                    We have tried to make this as intuitive and simple as possible. - If you are ever stuck or forget something make sure to read our tooltips.
                </div>
            ),
            action: () => {
                window.openMasksAccordion?.(true);
            }
        },
        {
            selector: '.planStep-masks',
            content: () => (
                <div>
                    <br />
                    Masks are used to define calculation points within your plan by masking in or out.
                    <br /><br />
                    The way we do this is by left clicking to mask in points and right clicking to mask out points.
                    <br /><br />
                    You have access to Square, Circular and Polygon masks.
                </div>
            ),
        },
        {
            selector: '.planStep-masks',
            content: () => (
                <div>
                    <br />
                    The order of masks is very important. Masks at the top of the list are lower priority than masks at the bottom of the list.
                    <br /><br />
                    This means that if a point is masked in by one mask but masked out by another mask lower in the list, the point will be masked out and vice versa.
                    <br /><br />
                    We would recommend you experiment with this to get a better understanding of how it works.
                </div>
            ),
        },
        {
            selector: '.planStep-masks',
            content: () => (
                <div>
                    <br />
                    You can edit, lock and delete each mask.
                    <br /><br />
                    Make use of the drag handle to reorder your masks and change their priority.
                </div>
            ),
        },
        {
            selector: '.planStep-masks',
            content: () => (
                <div>
                    <br />
                    To use the polygon mask you either left click or right click to change the mode.
                    <br /><br />
                    Then use your left click to lock in points and draw your mask.
                    <br /><br />
                    You finish by clicking close to the first point you made. - You can delete/add and move points after finishing creation of the mask.
                </div>
            ),
        },
        {
            selector: '.planStep-canvas',
            content: () => (
                <div>
                    <br />
                    Try it now by creating a few masks on the drawing itself.
                    <br /><br />
                    Remember left click to mask in and right click to mask out. - I would recommend locking masks after creating and positioning them to avoid accidental changes.
                    <br /><br />
                    You can always edit your masks later if you make a mistake or want to change something.
                </div>
            ),
            position: [1200, 525],
            action: () => {
                window.openColumnTypeAccordion?.(true);
            }
        },
        {
            selector: '.planStep-canvas',
            content: () => (
                <div>
                    <br />
                    You can also try adding some columns now using the column type you created earlier.
                    <br /><br />
                    Select your column type from the right toolbar and then click anywhere on the drawing to place it.
                    <br /><br />
                    You can always edit your columns later if you make a mistake or want to change something.
                </div>
            ),
            position: [1200, 525],
            action: () => {
                window.openSaveConfigAccordion?.(true);
            }
        },
        {
            selector: '.planStep-save-config',
            content: () => (
                <div>
                    <br />
                    When you are happy with your plan setup you can save your configuration by clicking this button and entering a name for it.
                    <br /><br />
                    This will allow you to load your plan setup later on or in another project.
                </div>
            ),
        },
        {
            selector: '.planStep-save-config',
            content: () => (
                <div>
                    <br />
                    You are also able to import, export and delete your saved configurations.
                    <br /><br />
                    The update button will overwrite your existing configuration with the current plan setup.
                </div>
            ),
        },
        {
            content: () => (
                <div>
                    <br />
                    To wrap up, lets quickly go over the drawing files and layers.
                    <br /><br />
                    The drawing file section allows you to load or swap between existing dawing files previously imported as well as update or delete them.
                    <br /><br />
                    The layers section allows you to toggle the visibility of different layers within your drawing file.
                    <br /><br />
                    This is useful for complex drawings with multiple layers.
                </div>
            ),
        },
        {
            content: () => (
                <div>
                    <br />
                    After all of these steps you should be able to create a basic plan and explore the numerous settings available to you.
                    <br /><br />
                    At this point you can start creating reports and sharing your plans with others.
                </div>
            ),
            action: () => {
                window.openMasksAccordion?.(false);
                window.openSaveConfigAccordion?.(false);
                window.openColumnTypeAccordion?.(false);
                window.openColumnsAccordion?.(false);
                window.openGridsAccordion?.(false);
                window.openReportAccordion?.(true);
            }
        },
        {
            selector: '.planStep-reports',
            content: () => (
                <div>
                    <br />
                    To begin with reports create a new template from this section.
                    <br /><br />
                    Once you create a template you can select it again from the drop down list or create a new one with different settings.
                </div>
            ),
        },
        {
            selector: '.planStep-reports',
            content: () => (
                <div>
                    <br />
                    Once a report has been created you can edit, view and delete it from this section.
                    <br /><br />
                    You should also adjust the other settings to your needs before generating or viewing the report. - Viewing the report will show you what you receive when generating the report so these settings are important.
                </div>
            ),
        },
        {
            selector: '.planStep-reports',
            content: () => (
                <div>
                    <br />
                    Click generate to create your report and download it as a PDF file to your computer.
                    <br /><br />
                </div>
            ),
        },
        {
            content: () => (
                <div>
                    <br />
                    Add that concludes the tour of Plan.
                    <br /><br />
                    You are now ready to create and share your plans and reports.
                    <br /><br />
                    If you ever need help or a reminder of how something works make sure to use our tooltips or help function.
                    <br /><br />
                    Thank you for taking the time to complete the tour and enjoy using Plan!
                </div>
            ),
        },

    ]
}

// Use to advance one step in the tour
//dispatch(tourActions.UpdateCurrentStep({ currentStep: state.EnabledTour.currentStep + 1 })) }
//setTimeout(() => { , 50);

export default steps;